import { type PaginationState } from '@tanstack/react-table'
import { type Dispatch, type FC, type SetStateAction } from 'react'

import HasReceiptsFilter from 'components/admin/HasReceiptsFilter'
import SubstantiationMedicalCareFilter from 'components/admin/SubstantiationMedicalCareFilter'
import SubstantiationOngoingCoverageFilter from 'components/admin/SubstantiationOngoingCoverageFilter'
import SubstantiationReviewStatusFilter from 'components/admin/SubstantiationReviewStatusFilter'
import UnitCardTransactionFilter from 'components/admin/UnitCardTransactionFilter'
import AfterDateFilter from 'components/brokers/needs_attention/AfterDateFilter'
import AssigneesFilter from 'components/brokers/needs_attention/AssigneesFilter'
import BeforeDateFilter from 'components/brokers/needs_attention/BeforeDateFilter'
import EmployerFilter from 'components/brokers/needs_attention/EmployerFilter'
import MemberFilter from 'components/brokers/needs_attention/MemberFilter'
import VipEmployerFilter from 'components/brokers/needs_attention/VipEmployerFilter'
import {
  type MultipleSelectedValuesFilter,
  type SingleSelectedValueFilter,
} from 'components/brokers/types'
import { type SubstantiationsFilters } from 'components/hooks/substantiations/useGetSubstantiations'
import EnterpriseFiltersView, { type FilterConfigurations } from 'components/reusable/filters/EnterpriseFiltersView'
import { type ComboboxFilterGroup } from 'components/reusable/table/ComboboxMultiSelectFilter'

export type SubstantiationFilterGroupType =
  | 'Date'
  | 'Associations'
  | 'Substantiation'
  | 'Triaging'

type SingleSelectFilterComponents = Record<
  string,
  FC<SingleSelectedValueFilter>
>
type MultiSelectFilterComponents = Record<
  string,
  FC<MultipleSelectedValuesFilter>
>

export const substantiationSingleSelectFilters: SingleSelectFilterComponents = {
  after_date: AfterDateFilter,
  before_date: BeforeDateFilter,
  vip_employer: VipEmployerFilter,
  has_receipts: HasReceiptsFilter,
  payment_method_unit_card: UnitCardTransactionFilter,
}

export const substantiationMultiSelectFilters: MultiSelectFilterComponents = {
  review_statuses: SubstantiationReviewStatusFilter,
  medical_care: SubstantiationMedicalCareFilter,
  ongoing_coverage: SubstantiationOngoingCoverageFilter,
  employer_ids: EmployerFilter,
  member_ids: MemberFilter,
  broker_ids: AssigneesFilter,
}

export const substantiationFilterConfigurations: FilterConfigurations<
  SubstantiationsFilters,
  SubstantiationFilterGroupType
> = {
  after_date: {
    key: 'after_date',
    label: 'After Date',
    group: 'Date',
    isMultiValue: false,
  },
  before_date: {
    key: 'before_date',
    label: 'Before Date',
    group: 'Date',
    isMultiValue: false,
  },
  review_statuses: {
    key: 'review_statuses',
    label: 'Review Status',
    group: 'Substantiation',
    isMultiValue: true,
  },
  medical_care: {
    key: 'medical_care',
    label: 'Medical Care',
    group: 'Substantiation',
    isMultiValue: true,
  },
  ongoing_coverage: {
    key: 'ongoing_coverage',
    label: 'Ongoing Coverage',
    group: 'Substantiation',
    isMultiValue: true,
  },
  employer_ids: {
    key: 'employer_ids',
    label: 'Employer',
    group: 'Associations',
    isMultiValue: true,
  },
  member_ids: {
    key: 'member_ids',
    label: 'Member',
    group: 'Associations',
    isMultiValue: true,
  },
  vip_employer: {
    key: 'vip_employer',
    label: 'VIP Employer',
    group: 'Associations',
    isMultiValue: false,
  },
  broker_ids: {
    key: 'broker_ids',
    label: 'Assignee',
    group: 'Triaging',
    isMultiValue: true,
  },
  has_receipts: {
    key: 'has_receipts',
    label: 'Has Receipts',
    group: 'Substantiation',
    isMultiValue: false,
  },
  payment_method_unit_card: {
    key: 'payment_method_unit_card',
    label: 'Unit Card Transaction',
    group: 'Substantiation',
    isMultiValue: false,
  },
}

export const substantiationDefaultPinnedOptionsByFilterValue = {
  review_statuses: [],
  medical_care: [],
  ongoing_coverage: [],
  employer_ids: [],
  member_ids: [],
  broker_ids: [{ label: 'Unassigned', value: 'unassigned' }],
}

export const getSubstantiationFiltersConfigurationSortValue = (
  filterConfiguration: ComboboxFilterGroup<string>
): number => {
  switch (filterConfiguration.groupLabel) {
    case 'Triaging':
      return 0
    case 'Substantiation':
      return 1
    case 'Date':
      return 2
    case 'Associations':
      return 3
    default:
      return 4
  }
}

type SubstantiationFiltersProps = {
  filters: SubstantiationsFilters
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const SubstantiationFilters = ({
  filters,
  setFilters,
  pagination,
  setPagination,
}: SubstantiationFiltersProps) => {
  return (
    <EnterpriseFiltersView
      filters={filters}
      setFilters={setFilters}
      pagination={pagination}
      setPagination={setPagination}
      filterConfigurations={substantiationFilterConfigurations}
      singleSelectFilters={substantiationSingleSelectFilters}
      multiSelectFilters={substantiationMultiSelectFilters}
      defaultPinnedOptionsByFilterValue={
        substantiationDefaultPinnedOptionsByFilterValue
      }
      getFiltersConfigurationSortValue={
        getSubstantiationFiltersConfigurationSortValue
      }
    />
  )
}

export default SubstantiationFilters
